import { CheckOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";

import styles from "./WizardStepper.module.css";

export type WizardStep<Id extends string = string> = {
  id: Id;
  label: string;
};

export type WizardStepperProps<Id extends string = string> = {
  steps: ReadonlyArray<WizardStep<Id>>;
  /** Id of the step currently in view. Everything before it renders as
   *  completed (a check icon); everything after as upcoming. */
  activeStepId: Id;
  ariaLabel: string;
  /** Prefix for each step's element id + `data-testid` (e.g. `create-season-step`).
   *  The container itself is exposed as `${idPrefix}s`. */
  idPrefix: string;
  /** When provided, the steps render as an interactive ARIA tablist wired to
   *  this handler (Create Season, Create Monthly Challenge). Omit it for a
   *  read-only progress indicator rendered as an ordered list. */
  onStepClick?: (stepId: Id) => void;
  /** `aria-controls` target prefix for the interactive tabs, i.e. the id of the
   *  step panel each tab drives. Only used in the interactive variant. */
  panelIdPrefix?: string;
};

/**
 * Shared Game Pass wizard step indicator.
 *
 * Single source of truth for the numbered "Season Overview → Challenges →
 * Schedule" stepper used across the Game Pass configuration wizards, so the
 * Create Season and Create Monthly Challenge flows stay visually identical.
 * Interactive (tablist) vs read-only (ordered list) is selected by whether an
 * `onStepClick` handler is supplied.
 */
function WizardStepper<Id extends string = string>({
  steps,
  activeStepId,
  ariaLabel,
  idPrefix,
  onStepClick,
  panelIdPrefix,
}: WizardStepperProps<Id>) {
  const activeIndex = steps.findIndex((step) => step.id === activeStepId);

  const renderStepInner = (
    label: string,
    index: number,
    isActive: boolean,
    isCompleted: boolean,
  ): ReactNode => {
    const numberClassName = [
      styles.stepNumber,
      isActive ? styles.stepNumberActive : "",
      isCompleted ? styles.stepNumberCompleted : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <>
        <span className={numberClassName} aria-hidden="true">
          {isCompleted ? (
            <CheckOutlined className={styles.stepCheckIcon} />
          ) : (
            index + 1
          )}
        </span>
        <span className={styles.stepLabel}>{label}</span>
      </>
    );
  };

  if (onStepClick) {
    return (
      <div
        className={styles.steps}
        role="tablist"
        aria-label={ariaLabel}
        data-testid={`${idPrefix}s`}
      >
        {steps.map((step, index) => {
          const isActive = step.id === activeStepId;
          const isCompleted = activeIndex >= 0 && index < activeIndex;
          const stepClassName = [
            styles.step,
            styles.stepInteractive,
            isActive ? styles.stepActive : "",
            isCompleted ? styles.stepCompleted : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              id={`${idPrefix}-${step.id}`}
              aria-selected={isActive}
              aria-controls={
                panelIdPrefix ? `${panelIdPrefix}-${step.id}` : undefined
              }
              tabIndex={isActive ? 0 : -1}
              className={stepClassName}
              onClick={() => onStepClick(step.id)}
              data-testid={`${idPrefix}-${step.id}`}
            >
              {renderStepInner(step.label, index, isActive, isCompleted)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <ol
      className={styles.steps}
      aria-label={ariaLabel}
      data-testid={`${idPrefix}s`}
    >
      {steps.map((step, index) => {
        const isActive = step.id === activeStepId;
        const isCompleted = activeIndex >= 0 && index < activeIndex;
        const stepClassName = [
          styles.step,
          isActive ? styles.stepActive : "",
          isCompleted ? styles.stepCompleted : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <li
            key={step.id}
            className={stepClassName}
            aria-current={isActive ? "step" : undefined}
            data-testid={`${idPrefix}-${step.id}`}
          >
            {renderStepInner(step.label, index, isActive, isCompleted)}
          </li>
        );
      })}
    </ol>
  );
}

export default WizardStepper;
